import { useState, useCallback, useRef, useEffect } from 'react';
import * as transcriptionsApi from '../api/transcriptions';
import { handleApiError } from '../api/client';

export interface UseTranscriptionsState {
  transcriptions: transcriptionsApi.TranscriptionResponse[];
  totalTranscriptions: number;
  totalPages: number;
  currentPage: number;
  isLoading: boolean;
  error: string | null;
}

export interface UseTranscriptionsReturn extends UseTranscriptionsState {
  fetchTranscriptions: (params?: transcriptionsApi.ListTranscriptionsParams) => Promise<void>;
  createTranscription: (data: transcriptionsApi.CreateTranscriptionRequest) => Promise<transcriptionsApi.TranscriptionResponse>;
  getTranscription: (id: string) => Promise<transcriptionsApi.TranscriptionResponse>;
  getTranscriptionResult: (id: string) => Promise<transcriptionsApi.TranscriptionResultResponse>;
  translateTranscription: (id: string, targetLanguage: string) => Promise<transcriptionsApi.TranslateTranscriptionResponse>;
  cancelTranscription: (id: string) => Promise<void>;
  retryTranscription: (id: string) => Promise<void>;
  deleteTranscription: (id: string) => Promise<void>;
  refreshTranscriptions: () => Promise<void>;
}

export function useTranscriptions(): UseTranscriptionsReturn {
  const [state, setState] = useState<UseTranscriptionsState>({
    transcriptions: [],
    totalTranscriptions: 0,
    totalPages: 0,
    currentPage: 1,
    isLoading: false,
    error: null,
  });

  const [lastParams, setLastParams] = useState<transcriptionsApi.ListTranscriptionsParams | undefined>();

  const fetchTranscriptions = useCallback(async (params?: transcriptionsApi.ListTranscriptionsParams) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    setLastParams(params);

    try {
      const response = await transcriptionsApi.listTranscriptions(params);
      setState({
        transcriptions: response.transcriptions,
        totalTranscriptions: response.total,
        totalPages: response.total_pages,
        currentPage: response.page,
        isLoading: false,
        error: null,
      });
    } catch (err) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: handleApiError(err),
      }));
    }
  }, []);

  const refreshTranscriptions = useCallback(async () => {
    await fetchTranscriptions(lastParams);
  }, [fetchTranscriptions, lastParams]);

  const createTranscription = useCallback(
    async (data: transcriptionsApi.CreateTranscriptionRequest) => {
      const transcription = await transcriptionsApi.createTranscription(data);
      await refreshTranscriptions();
      return transcription;
    },
    [refreshTranscriptions]
  );

  const getTranscription = useCallback(async (id: string) => {
    return await transcriptionsApi.getTranscription(id);
  }, []);

  const getTranscriptionResult = useCallback(async (id: string) => {
    return await transcriptionsApi.getTranscriptionResult(id);
  }, []);

  const translateTranscription = useCallback(async (id: string, targetLanguage: string) => {
    return await transcriptionsApi.translateTranscription(id, targetLanguage);
  }, []);

  const cancelTranscription = useCallback(
    async (id: string) => {
      await transcriptionsApi.cancelTranscription(id);
      await refreshTranscriptions();
    },
    [refreshTranscriptions]
  );

  const retryTranscription = useCallback(
    async (id: string) => {
      await transcriptionsApi.retryTranscription(id);
      await refreshTranscriptions();
    },
    [refreshTranscriptions]
  );

  const deleteTranscription = useCallback(
    async (id: string) => {
      await transcriptionsApi.deleteTranscription(id);
      setState((prev) => ({
        ...prev,
        transcriptions: prev.transcriptions.filter((t) => t.id !== id),
        totalTranscriptions: prev.totalTranscriptions - 1,
      }));
    },
    []
  );

  return {
    ...state,
    fetchTranscriptions,
    createTranscription,
    getTranscription,
    getTranscriptionResult,
    translateTranscription,
    cancelTranscription,
    retryTranscription,
    deleteTranscription,
    refreshTranscriptions,
  };
}

// Hook for polling a single transcription status
export function useTranscriptionPolling(
  transcriptionId: string | null,
  onStatusChange?: (status: transcriptionsApi.TranscriptionStatusResponse) => void
) {
  const [status, setStatus] = useState<transcriptionsApi.TranscriptionStatusResponse | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const intervalRef = useRef<number | null>(null);
  const onStatusChangeRef = useRef(onStatusChange);

  // Keep the callback ref updated
  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  }, [onStatusChange]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPolling(false);
  }, []);

  const startPolling = useCallback(
    (intervalMs: number = 5000) => {
      if (!transcriptionId) return;

      // Clear any existing interval first
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      setIsPolling(true);

      const poll = async () => {
        try {
          const newStatus = await transcriptionsApi.getTranscriptionStatus(transcriptionId);
          setStatus(newStatus);
          onStatusChangeRef.current?.(newStatus);

          // Stop polling if completed or failed
          if (newStatus.status === 'completed' || newStatus.status === 'failed' || newStatus.status === 'cancelled') {
            stopPolling();
          }
        } catch {
          // Stop polling on error
          stopPolling();
        }
      };

      // Poll immediately
      poll();

      // Set up interval
      intervalRef.current = window.setInterval(poll, intervalMs);
    },
    [transcriptionId, stopPolling]
  );

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    status,
    isPolling,
    startPolling,
    stopPolling,
  };
}

export default useTranscriptions;
